import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { loadAllFilterEntities } from '@api/filters';
import { createSavedReport, updateSavedReport } from '@api/savedReports';
import { downloadExcelExport, downloadPdfExport, PDF_ORIENTATION } from '@api/exports';
import { useUser } from '@common/contexts/UserContext';
import { ChartOptionsProvider, useChartOptions } from '@common/contexts/ChartOptionsContext';
import { useExportAction } from '@common/hooks/useAsyncAction';
import { Button } from '@common/components';
import Breadcrumbs from '@common/components/Breadcrumbs';
import Header from '@common/components/Header';
import DropdownMenu from '@common/components/DropdownMenu';
import { DownloadIcon, PdfIcon, ExcelIcon, BackArrowIcon } from '@common/components/icons';
import { calculateGridLayouts, GRID_CONFIG } from '@reports/utils/dashboard/layoutUtils';
import { useWidgetData } from '../../hooks/useWidgetData';
import WidgetRenderer from '../Widgets/WidgetRenderer';
import DashboardFilters from './DashboardFilters';
import SaveReportModal from '../SaveReportModal/SaveReportModal';
import * as styles from './DashboardViewer.module.css';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

function DashboardViewerInner({
  dashboard,
  onBack,
  initialFilters, // New prop from configuration step
  availableEntities: propAvailableEntities,
  dashboardFilters: propDashboardFilters,
  onFilterChange,
  onRefresh,
  showFilters = true,
  isEditMode = false,
  savedReportId = null,
  savedReportName = '',
  isSavedReportView = false, // New prop: viewing a saved report (read-only)
  embedMode = false, // Embed mode: shows only action bar and dashboard grid
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { execute: executeExport, loading: exporting } = useExportAction();
  const getChartOptions = useChartOptions();

  // Payload size guard: drop widgetChartOptions if too large (5 MB) so the
  // backend falls back to its own data-fetching logic.
  const getSafeChartOptions = useCallback(() => {
    const MAX_CHART_OPTIONS_SIZE = 5 * 1024 * 1024; // 5 MB
    const opts = getChartOptions();
    if (!opts) return null;
    try {
      const serialized = JSON.stringify(opts);
      if (serialized.length > MAX_CHART_OPTIONS_SIZE) {
        // eslint-disable-next-line no-console
        console.warn(
          `widgetChartOptions payload too large (${(serialized.length / (1024 * 1024)).toFixed(1)} MB) — omitting from export request.`,
        );
        return null;
      }
    } catch {
      // Non-serializable options — fall back to backend logic
      return null;
    }
    return opts;
  }, [getChartOptions]);

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savingChanges, setSavingChanges] = useState(false);
  const [dashboardArgs, setDashboardArgs] = useState({});
  const [dashboardFilters, setDashboardFilters] = useState(
    initialFilters ||
      propDashboardFilters || {
        timeRange: null,
        timeFrom: null,
        timeTo: null,
        entities: {
          hosts: [],
          metrics: [],
          business_services: [],
          host_groups: [],
        },
        hostCategories: [],
        metricCategories: [],
      },
  );

  const [availableEntities, setAvailableEntities] = useState(
    propAvailableEntities || {
      hosts: [],
      metrics: [],
      business_services: [],
      host_groups: [],
    },
  );

  // Per-widget React Query — grid renders immediately; each widget shows loading until its data resolves
  const {
    widgets,
    refreshing,
    refetch: refetchWidgets,
  } = useWidgetData(dashboard, dashboardFilters);

  // Extract dashboard arguments from widget queries
  useEffect(() => {
    const args = {};
    const widgetsToProcess = dashboard?.widgets || [];
    widgetsToProcess.forEach((widget) => {
      if (widget.config?.query) {
        const matches = widget.config.query.match(/\{\{(\w+)\}\}/g);
        if (matches) {
          matches.forEach((match) => {
            const argName = match.replace(/\{\{|\}\}/g, '');
            if (!args[argName]) {
              args[argName] = '';
            }
          });
        }
      }
    });
    setDashboardArgs(args);
  }, [dashboard?.widgets]);

  // Sync initialFilters prop with dashboardFilters state
  useEffect(() => {
    if (initialFilters) {
      setDashboardFilters(initialFilters);
    }
  }, [initialFilters]);

  // Load available entities for filters (only if filters are shown and entities not provided)
  useEffect(() => {
    if (propAvailableEntities) {
      setAvailableEntities(propAvailableEntities);
      return;
    }

    // Only load entities if filters are shown
    if (!showFilters) {
      return;
    }

    const loadEntities = async () => {
      try {
        const entities = await loadAllFilterEntities();
        setAvailableEntities(entities);
      } catch {
        setAvailableEntities({
          hosts: [],
          metrics: [],
          business_services: [],
          host_groups: [],
        });
      }
    };

    loadEntities();
  }, [propAvailableEntities, showFilters]);

  // Handle filter changes
  const handleFilterChange = useCallback(
    (newFilters) => {
      // Filter changed
      setDashboardFilters(newFilters);

      // Notify parent component if callback provided
      if (onFilterChange) {
        onFilterChange(newFilters);
      }
    },
    [onFilterChange],
  );

  // Handle refresh - invalidates all queries so both parent-managed and
  // internally-managed widgets (tables) re-fetch their data
  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['table-widget'] });
    if (onRefresh) {
      onRefresh();
    } else {
      refetchWidgets();
    }
  }, [queryClient, onRefresh, refetchWidgets]);

  const handleArgumentChange = (argName, value) => {
    setDashboardArgs((prev) => ({
      ...prev,
      [argName]: value,
    }));
  };

  const handleRefreshData = (e) => {
    // Prevent default to avoid any form submission
    if (e) {
      e.preventDefault();
    }
    // Use the new refresh handler
    handleRefresh();
  };

  // Helper to resolve entity IDs to names for export display
  // Creates maps with both number and string keys to handle type mismatches
  // Loads entities on demand if not already available (e.g., when showFilters=false)
  const resolveEntityNames = useCallback(async () => {
    const entities = dashboardFilters?.entities || {};
    const result = {};

    // Check if we need to load any entities
    const needsHosts = entities.hosts?.length && !availableEntities.hosts?.length;
    const needsMetrics = entities.metrics?.length && !availableEntities.metrics?.length;
    const needsBusinessServices = entities.business_services?.length && !availableEntities.business_services?.length;
    const needsHostGroups = entities.host_groups?.length && !availableEntities.host_groups?.length;

    // Load entities on demand if needed
    let entitiesToUse = availableEntities;
    if (needsHosts || needsMetrics || needsBusinessServices || needsHostGroups) {
      try {
        entitiesToUse = await loadAllFilterEntities();
      } catch {
        entitiesToUse = availableEntities;
      }
    }

    // Helper to create a map that handles both number and string ID lookups
    const createIdMap = (items) => {
      const map = new Map();
      items.forEach((item) => {
        map.set(item.id, item.name);
        map.set(String(item.id), item.name);
      });
      return map;
    };

    if (entities.hosts?.length && entitiesToUse.hosts?.length) {
      const hostMap = createIdMap(entitiesToUse.hosts);
      result.hosts = entities.hosts.map((id) => hostMap.get(id) || hostMap.get(Number(id)) || `Host ${id}`);
    }
    if (entities.metrics?.length && entitiesToUse.metrics?.length) {
      // Create map for ID lookup
      const metricMap = new Map();
      entitiesToUse.metrics.forEach((m) => {
        metricMap.set(m.id, m);
        metricMap.set(String(m.id), m);
      });

      // Resolve all metrics first
      const resolvedMetricData = entities.metrics.map((id) => metricMap.get(id) || metricMap.get(Number(id)));

      // Only include if ALL metrics were resolved; otherwise let backend handle it
      if (resolvedMetricData.every((m) => m !== undefined)) {
        // Group metrics by host: "HostName (Metric1, Metric2)"
        const hostToMetrics = new Map();
        const noHostMetrics = [];

        resolvedMetricData.forEach((m) => {
          if (m.hostName) {
            const metrics = hostToMetrics.get(m.hostName) || [];
            metrics.push(m.name);
            hostToMetrics.set(m.hostName, metrics);
          } else {
            noHostMetrics.push(m.name);
          }
        });

        // Build grouped output
        const grouped = [];
        hostToMetrics.forEach((metrics, hostName) => {
          grouped.push(`${hostName} (${metrics.join(', ')})`);
        });
        grouped.push(...noHostMetrics);

        result.metrics = grouped;
      }
    }
    if (entities.business_services?.length && entitiesToUse.business_services?.length) {
      const bsMap = createIdMap(entitiesToUse.business_services);
      result.business_services = entities.business_services.map((id) => bsMap.get(id) || bsMap.get(Number(id)) || `Business Service ${id}`);
    }
    if (entities.host_groups?.length && entitiesToUse.host_groups?.length) {
      const hgMap = createIdMap(entitiesToUse.host_groups);
      result.host_groups = entities.host_groups.map((id) => hgMap.get(id) || hgMap.get(Number(id)) || `Host Group ${id}`);
    }

    return result;
  }, [dashboardFilters?.entities, availableEntities]);

  // Build filters for export, adding timezone context for timestamp formatting
  const buildExportFilters = useCallback(
    async () => {
      const entityNames = await resolveEntityNames();
      const filters = { ...dashboardFilters, entityNames };

      // Pass the timezone type so the backend knows how to format timestamps
      const timezoneType = dashboardFilters?.timeRange?.timezone;
      if (timezoneType === 'local' || timezoneType === 'host') {
        filters.timezoneType = timezoneType;
      }

      // When user selects "Local Time Zone", pass the browser's IANA timezone
      // so the backend can format dates in the user's local timezone
      if (timezoneType === 'local') {
        try {
          filters.localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch {
          // Fallback: if Intl API is not available, don't add localTimezone
        }
      }

      return filters;
    },
    [dashboardFilters, resolveEntityNames],
  );

  // Export handlers
  const handleExportPdf = useCallback(
    async (orientation = PDF_ORIENTATION.PORTRAIT) => {
      if (!dashboard?.id) return;

      const filters = await buildExportFilters();
      const widgetChartOptions = getSafeChartOptions();
      await executeExport(() =>
        downloadPdfExport({
          dashboardId: dashboard.id,
          filters,
          userName: user?.contact_name || 'Unknown',
          savedReportName: savedReportName || dashboard.name,
          widgetChartOptions,
          options: {
            orientation,
          },
        }),
      );
    },
    [dashboard?.id, dashboard?.name, user?.contact_name, savedReportName, executeExport, buildExportFilters, getSafeChartOptions],
  );

  const handleExportExcel = useCallback(async () => {
    if (!dashboard?.id) return;

    const filters = await buildExportFilters();
    const widgetChartOptions = getSafeChartOptions();
    await executeExport(() =>
      downloadExcelExport({
        dashboardId: dashboard.id,
        filters,
        userName: user?.contact_name || 'Unknown',
        savedReportName: savedReportName || dashboard.name,
        widgetChartOptions,
      }),
    );
  }, [dashboard?.id, dashboard?.name, user?.contact_name, savedReportName, executeExport, buildExportFilters, getSafeChartOptions]);

  // Handle opening save modal
  const handleOpenSaveModal = useCallback(() => {
    setSaveError(null);
    setShowSaveModal(true);
  }, []);

  // Handle saving the report (for new reports)
  const handleSaveReport = useCallback(
    async ({ name, description }) => {
      setSaving(true);
      setSaveError(null);

      try {
        const result = await createSavedReport({
          name,
          description,
          dashboardId: dashboard?.id,
          configuration: dashboardFilters,
        });

        if (result.status === 'success') {
          setShowSaveModal(false);
          // Optionally show success message or navigate
        } else {
          setSaveError(result.message || t('savedReports.errors.saveFailed'));
        }
      } catch {
        setSaveError(t('savedReports.errors.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [dashboard, dashboardFilters, t],
  );

  // Handle saving changes to an existing saved report (edit mode)
  const handleSaveChanges = useCallback(async () => {
    if (!savedReportId) return;

    setSavingChanges(true);

    try {
      const result = await updateSavedReport(savedReportId, {
        configuration: dashboardFilters,
      });

      if (result.status === 'success') {
        // Navigate back to saved reports page
        navigate('/saved');
      } else {
        console.error('Failed to save changes:', result.message);
      }
    } catch (error) {
      console.error('Failed to save changes:', error);
    } finally {
      setSavingChanges(false);
    }
  }, [savedReportId, dashboardFilters, navigate]);

  // Widget layouts using shared utility for consistency with Editor
  const layouts = React.useMemo(() => calculateGridLayouts(widgets), [widgets]);

  // Export menu items with PDF orientation options
  const exportMenuItems = [
    {
      key: 'pdf-portrait',
      label: exporting ? t('dashboard.exporting') : t('dashboard.exportPdfPortrait'),
      icon: <PdfIcon size={16} />,
      onClick: () => handleExportPdf(PDF_ORIENTATION.PORTRAIT),
      disabled: exporting,
    },
    {
      key: 'pdf-landscape',
      label: exporting ? t('dashboard.exporting') : t('dashboard.exportPdfLandscape'),
      icon: <PdfIcon size={16} />,
      onClick: () => handleExportPdf(PDF_ORIENTATION.LANDSCAPE),
      disabled: exporting,
    },
    {
      key: 'excel',
      label: exporting ? t('dashboard.exporting') : t('dashboard.exportExcel'),
      icon: <ExcelIcon size={16} />,
      onClick: handleExportExcel,
      disabled: exporting,
    },
  ];

  // Header action buttons (Refresh + Export)
  const headerActions = (
    <>
      <Button
        variant="outline"
        size="small"
        onClick={handleRefreshData}
        title={t('dashboard.refreshDashboard')}
        disabled={refreshing}
      >
        {t('dashboard.refresh')}
      </Button>
      <DropdownMenu label={t('dashboard.export')} icon={<DownloadIcon size={14} />} items={exportMenuItems} />
    </>
  );

  // Determine if we should show saved report name (either in edit mode or viewing a saved report)
  const showSavedReportInfo = savedReportName && savedReportName.length > 0;
  const displayName = showSavedReportInfo ? savedReportName : dashboard?.name || t('dashboard.view');

  return (
    <div className={`${styles.container} ${embedMode ? styles.embedMode : ''}`}>
      {/* Breadcrumbs - hidden in embed mode */}
      {!embedMode && (
        <Breadcrumbs
          items={[
            { label: t('reports.pageTitle'), path: '/' },
            ...(showSavedReportInfo ? [{ label: t('savedReports.title'), path: '/saved' }] : []),
            { label: displayName },
          ]}
        />
      )}

      {/* Header with action buttons bar - hidden in embed mode */}
      {!embedMode && (
        <Header subtitle={displayName} actions={headerActions}>
          {t('reports.pageTitle')}
        </Header>
      )}

      {/* Action bar for embed mode - only Refresh and Export */}
      {embedMode && <div className={styles.embedActionBar}>{headerActions}</div>}

      {/* Save Report Modal - hidden in embed mode */}
      {!embedMode && (
        <SaveReportModal
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          onSave={handleSaveReport}
          saving={saving}
          error={saveError}
          defaultName={dashboard?.name ? `${dashboard.name} - ${new Date().toLocaleDateString()}` : ''}
        />
      )}

      {/* Dashboard Filters - hidden in embed mode */}
      {showFilters && !embedMode && (
        <DashboardFilters
          filters={dashboardFilters}
          onFiltersChange={handleFilterChange}
          availableEntities={availableEntities}
        />
      )}

      {/* Dashboard Arguments - hidden in embed mode */}
      {Object.keys(dashboardArgs).length > 0 && !embedMode && (
        <div className={styles.argumentsPanel}>
          <h3>{t('dashboard.dashboardParameters')}</h3>
          <div className={styles.argumentsGrid}>
            {Object.entries(dashboardArgs).map(([argName, value]) => (
              <div key={argName} className={styles.argumentField}>
                <label className="form-label" htmlFor={`arg-${argName}`}>
                  {argName}
                </label>
                <input
                  id={`arg-${argName}`}
                  type="text"
                  className="form-input"
                  value={value}
                  onChange={(e) => handleArgumentChange(argName, e.target.value)}
                  placeholder={`${t('dashboard.enterParam')} ${argName}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dashboard Grid */}
      <div className={styles.dashboardGrid}>
        <ResponsiveGridLayout
          className={styles.layout}
          layouts={layouts}
          breakpoints={GRID_CONFIG.breakpoints}
          cols={GRID_CONFIG.cols}
          rowHeight={GRID_CONFIG.rowHeight}
          margin={GRID_CONFIG.margin}
          containerPadding={GRID_CONFIG.containerPadding}
          isDraggable={false}
          isResizable={false}
          compactType={null}
        >
          {widgets.map((widget) => (
            <div key={String(widget.id)} className={styles.widgetContainer}>
              <WidgetRenderer widget={widget} dashboardArgs={dashboardArgs} dashboardFilters={dashboardFilters} />
            </div>
          ))}
        </ResponsiveGridLayout>
      </div>

      {/* Footer with action buttons - hidden in embed mode */}
      {!embedMode && (
        <div className={styles.footer}>
          {isSavedReportView && (
            // Viewing a saved report (read-only) - only show Close button
            <Button variant="primary" onClick={onBack}>
              {t('common.close')}
            </Button>
          )}
          {isEditMode && !isSavedReportView && (
            // Edit mode - show Back and Save Changes buttons
            <>
              <Button variant="outline" onClick={onBack}>
                <BackArrowIcon width="14" height="14" />
                {t('common.back')}
              </Button>
              <Button variant="primary" onClick={handleSaveChanges} disabled={savingChanges}>
                {savingChanges ? t('common.saving') : t('savedReports.modal.saveChanges')}
              </Button>
            </>
          )}
          {!isEditMode && !isSavedReportView && (
            // New report - show Back and Save buttons
            <>
              <Button variant="outline" onClick={onBack}>
                <BackArrowIcon width="14" height="14" />
                {t('common.back')}
              </Button>
              <Button variant="primary" onClick={handleOpenSaveModal}>
                {t('common.save')}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

DashboardViewerInner.propTypes = {
  dashboard: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    widgets: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  onBack: PropTypes.func.isRequired,
  initialFilters: PropTypes.shape({
    timeRange: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    timeFrom: PropTypes.number,
    timeTo: PropTypes.number,
    entities: PropTypes.shape({}),
  }),
  availableEntities: PropTypes.shape({
    hosts: PropTypes.arrayOf(PropTypes.shape({})),
    metrics: PropTypes.arrayOf(PropTypes.shape({})),
    business_services: PropTypes.arrayOf(PropTypes.shape({})),
    host_groups: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  dashboardFilters: PropTypes.shape({
    timeRange: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    timeFrom: PropTypes.number,
    timeTo: PropTypes.number,
    entities: PropTypes.shape({}),
  }),
  onFilterChange: PropTypes.func,
  onRefresh: PropTypes.func,
  showFilters: PropTypes.bool,
  isEditMode: PropTypes.bool,
  savedReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  savedReportName: PropTypes.string,
  isSavedReportView: PropTypes.bool,
  embedMode: PropTypes.bool,
};

DashboardViewerInner.defaultProps = {
  initialFilters: null,
  availableEntities: null,
  dashboardFilters: null,
  onFilterChange: null,
  onRefresh: null,
  showFilters: true,
  isEditMode: false,
  savedReportId: null,
  savedReportName: '',
  isSavedReportView: false,
  embedMode: false,
};

// Wrapper that provides the ChartOptionsContext so chart widgets can register
// their ECharts options and the export handlers can read them.
function DashboardViewer(props) {
  return (
    <ChartOptionsProvider>
      <DashboardViewerInner {...props} />
    </ChartOptionsProvider>
  );
}

export default DashboardViewer;
